import type {
  InstrumentDefinition,
  NormalledConnectionDefinition,
  PortDefinition,
} from "./instrument";
import { cableEndpoints, type Cable, type CableEndpoint } from "./patch";

export type NormalledRouteState = {
  route: NormalledConnectionDefinition;
  status: "active" | "broken";
  brokenByCableIds: string[];
};

export type SignalWarning = {
  id: string;
  kind: "loose-end" | "direction" | "signal-type" | "missing-port" | "fan-out";
  severity: "info" | "warning";
  message: string;
  cableId?: string;
  portIds: string[];
};

export type SignalTrace = {
  active: boolean;
  nodeIds: Set<string>;
  upstreamNodeIds: Set<string>;
  downstreamNodeIds: Set<string>;
  cableIds: Set<string>;
  normalledRouteIds: Set<string>;
  signalConnectionIds: Set<string>;
};

function cableLegs(cable: Cable): Array<[CableEndpoint, CableEndpoint]> {
  return [cable.endpointB, ...(cable.branches ?? [])].map((endpoint) => [
    cable.endpointA,
    endpoint,
  ]);
}

function cableTouchesPort(cable: Cable, portId: string) {
  return cableEndpoints(cable).some(
    (endpoint) => endpoint.kind === "port" && endpoint.portId === portId,
  );
}

export function getNormalledRouteStates(
  definition: InstrumentDefinition,
  cables: Cable[],
  controlValues: Readonly<Record<string, number>> = {},
): NormalledRouteState[] {
  return definition.normalledConnections
    .filter((route) => {
      if (!route.activeWhen) return true;
      const control = definition.controls.find(
        (candidate) => candidate.id === route.activeWhen?.controlId,
      );
      if (!control?.options?.length) return false;
      const value = controlValues[control.id] ?? control.defaultValue;
      const option = control.options.reduce((nearest, candidate, index) => {
        const candidateValue = candidate.value ?? index / Math.max(1, control.options!.length - 1);
        const nearestIndex = control.options!.indexOf(nearest);
        const nearestValue =
          nearest.value ?? nearestIndex / Math.max(1, control.options!.length - 1);
        return Math.abs(candidateValue - value) < Math.abs(nearestValue - value)
          ? candidate
          : nearest;
      });
      return Boolean(option && route.activeWhen.optionIds.includes(option.id));
    })
    .map((route) => {
      const breakPorts =
        route.breakCondition.kind === "destination-patched"
          ? [route.toId]
          : route.breakCondition.kind === "source-patched"
            ? [route.fromId]
            : route.breakCondition.kind === "either-patched"
              ? [route.fromId, route.toId]
              : [];
      const brokenByCableIds = cables
        .filter((cable) => breakPorts.some((portId) => cableTouchesPort(cable, portId)))
        .map((cable) => cable.id);
      return {
        route,
        status: brokenByCableIds.length ? "broken" : "active",
        brokenByCableIds,
      };
    });
}

function compatibleSignalTypes(a: PortDefinition, b: PortDefinition) {
  if (a.signalType === "unknown" || b.signalType === "unknown") return true;
  if (a.signalType === "mixed" || b.signalType === "mixed") return true;
  if (a.signalType === b.signalType) return true;
  const controlSignals = new Set(["cv", "gate", "clock", "expression"]);
  return controlSignals.has(a.signalType) && controlSignals.has(b.signalType);
}

export function validateSignalPatch(
  definition: InstrumentDefinition,
  cables: Cable[],
): SignalWarning[] {
  const ports = new Map(definition.ports.map((port) => [port.id, port]));
  const warnings: SignalWarning[] = [];

  for (const cable of cables) {
    for (const [endpointIndex, endpoint] of cableEndpoints(cable).entries()) {
      if (endpoint.kind !== "loose") continue;
      warnings.push({
        id: `${cable.id}-loose-${endpointIndex}`,
        kind: "loose-end",
        severity: "info",
        message: endpoint.label
          ? `Cable ${cable.number} ends at “${endpoint.label}”.`
          : `Cable ${cable.number} has an unlabelled loose end.`,
        cableId: cable.id,
        portIds: [],
      });
    }

    for (const [legIndex, [endpointA, endpointB]] of cableLegs(cable).entries()) {
      if (endpointA.kind !== "port" || endpointB.kind !== "port") continue;
      const a = ports.get(endpointA.portId);
      const b = ports.get(endpointB.portId);
      if (!a || !b) {
        warnings.push({
          id: `${cable.id}-missing-port-${legIndex}`,
          kind: "missing-port",
          severity: "warning",
          message: `Cable ${cable.number} references a patch point that is not in this instrument definition.`,
          cableId: cable.id,
          portIds: [endpointA.portId, endpointB.portId],
        });
        continue;
      }
      if (a.direction === b.direction && a.direction !== "bidirectional") {
        warnings.push({
          id: `${cable.id}-direction-${legIndex}`,
          kind: "direction",
          severity: "warning",
          message: `Cable ${cable.number} connects ${a.direction} to ${b.direction}; this may be intentional, but it has no clear source-to-destination direction.`,
          cableId: cable.id,
          portIds: [a.id, b.id],
        });
      }
      if (!compatibleSignalTypes(a, b)) {
        warnings.push({
          id: `${cable.id}-signal-type-${legIndex}`,
          kind: "signal-type",
          severity: "warning",
          message: `Cable ${cable.number} connects ${a.signalType} (${a.label}) to ${b.signalType} (${b.label}). Verify that this experimental routing is intended.`,
          cableId: cable.id,
          portIds: [a.id, b.id],
        });
      }
    }
  }

  const fanOut = new Map<string, Cable[]>();
  for (const cable of cables) {
    for (const endpoint of cableEndpoints(cable)) {
      if (endpoint.kind !== "port" || ports.get(endpoint.portId)?.direction !== "output") continue;
      fanOut.set(endpoint.portId, [...(fanOut.get(endpoint.portId) ?? []), cable]);
    }
  }
  for (const [portId, connected] of fanOut) {
    if (connected.length < 4) continue;
    warnings.push({
      id: `${portId}-fan-out`,
      kind: "fan-out",
      severity: "warning",
      message: `${ports.get(portId)?.label ?? portId} feeds ${connected.length} cables. Check the passive fan-out and voltage loading.`,
      portIds: [portId],
    });
  }

  return warnings;
}

type GraphEdge = {
  id: string;
  kind: "cable" | "normalled" | "internal";
  fromId: string;
  toId: string;
};

function addDirectedEdge(edges: GraphEdge[], edge: GraphEdge, bidirectional = false) {
  edges.push(edge);
  if (bidirectional) edges.push({ ...edge, fromId: edge.toId, toId: edge.fromId });
}

function physicalCableEdges(definition: InstrumentDefinition, cables: Cable[]) {
  const ports = new Map(definition.ports.map((port) => [port.id, port]));
  const edges: GraphEdge[] = [];
  for (const cable of cables) {
    for (const [endpointA, endpointB] of cableLegs(cable)) {
      if (endpointA.kind !== "port" || endpointB.kind !== "port") continue;
      const a = ports.get(endpointA.portId);
      const b = ports.get(endpointB.portId);
      if (!a || !b) continue;
      if (a.direction === "output" && b.direction !== "output") {
        addDirectedEdge(edges, { id: cable.id, kind: "cable", fromId: a.id, toId: b.id });
      } else if (b.direction === "output" && a.direction !== "output") {
        addDirectedEdge(edges, { id: cable.id, kind: "cable", fromId: b.id, toId: a.id });
      } else {
        addDirectedEdge(edges, { id: cable.id, kind: "cable", fromId: a.id, toId: b.id }, true);
      }
    }
  }
  return edges;
}

function traverse(
  seeds: string[],
  adjacency: Map<string, GraphEdge[]>,
  collect: (edge: GraphEdge) => void,
) {
  const visited = new Set(seeds);
  const queue = [...seeds];
  while (queue.length) {
    const current = queue.shift()!;
    for (const edge of adjacency.get(current) ?? []) {
      collect(edge);
      if (visited.has(edge.toId)) continue;
      visited.add(edge.toId);
      queue.push(edge.toId);
    }
  }
  return visited;
}

export function traceSignal(
  definition: InstrumentDefinition,
  cables: Cable[],
  selectedId: string | null,
  controlValues: Readonly<Record<string, number>> = {},
): SignalTrace {
  const empty: SignalTrace = {
    active: false,
    nodeIds: new Set(),
    upstreamNodeIds: new Set(),
    downstreamNodeIds: new Set(),
    cableIds: new Set(),
    normalledRouteIds: new Set(),
    signalConnectionIds: new Set(),
  };
  if (!selectedId) return empty;

  const normalStates = getNormalledRouteStates(definition, cables, controlValues);
  const edges = physicalCableEdges(definition, cables);
  for (const state of normalStates) {
    if (state.status === "active") {
      addDirectedEdge(edges, {
        id: state.route.id,
        kind: "normalled",
        fromId: state.route.fromId,
        toId: state.route.toId,
      });
    }
  }
  for (const connection of definition.signalConnections) {
    addDirectedEdge(
      edges,
      {
        id: connection.id,
        kind: "internal",
        fromId: connection.fromId,
        toId: connection.toId,
      },
      connection.bidirectional,
    );
  }

  const selectedCable = cables.find((cable) => cable.id === selectedId);
  const selectedNormal = definition.normalledConnections.find((route) => route.id === selectedId);
  const endpointIds = new Set([
    ...definition.ports.map((port) => port.id),
    ...definition.signalNodes.map((node) => node.id),
  ]);
  const seeds = selectedCable
    ? cableEndpoints(selectedCable)
        .filter((endpoint) => endpoint.kind === "port")
        .map((endpoint) => (endpoint.kind === "port" ? endpoint.portId : ""))
    : selectedNormal
      ? [selectedNormal.fromId, selectedNormal.toId]
      : endpointIds.has(selectedId)
        ? [selectedId]
        : [];
  if (!seeds.length) return empty;

  const forward = new Map<string, GraphEdge[]>();
  const reverse = new Map<string, GraphEdge[]>();
  for (const edge of edges) {
    forward.set(edge.fromId, [...(forward.get(edge.fromId) ?? []), edge]);
    reverse.set(edge.toId, [
      ...(reverse.get(edge.toId) ?? []),
      { ...edge, fromId: edge.toId, toId: edge.fromId },
    ]);
  }
  const cableIds = new Set<string>(selectedCable ? [selectedCable.id] : []);
  const normalledRouteIds = new Set<string>(selectedNormal ? [selectedNormal.id] : []);
  const signalConnectionIds = new Set<string>();
  const collect = (edge: GraphEdge) => {
    if (edge.kind === "cable") cableIds.add(edge.id);
    if (edge.kind === "normalled") normalledRouteIds.add(edge.id);
    if (edge.kind === "internal") signalConnectionIds.add(edge.id);
  };
  const downstreamNodeIds = traverse(seeds, forward, collect);
  const upstreamNodeIds = traverse(seeds, reverse, collect);
  const nodeIds = new Set([...downstreamNodeIds, ...upstreamNodeIds]);
  for (const cable of cables) {
    if (
      cableEndpoints(cable).some(
        (endpoint) => endpoint.kind === "port" && nodeIds.has(endpoint.portId),
      )
    ) {
      cableIds.add(cable.id);
    }
  }
  return {
    active: true,
    nodeIds,
    upstreamNodeIds,
    downstreamNodeIds,
    cableIds,
    normalledRouteIds,
    signalConnectionIds,
  };
}
